import { PetPicture, Text, ResponsiveLogo } from '@ui/components'
import React, { useEffect, useMemo } from "react"
import { useAppSelector } from '@front/store'
import { ReactComponent as PencilIcon } from '@/assets/icons/aena/aena_pencil.svg'
import { ReactComponent as Health } from '@/assets/icons/aena/aena_seguro.svg'
import { ReactComponent as IdBadge } from '@/assets/icons/aena/aena_documentacion.svg'
import { ReactComponent as Vaccine } from '@/assets/icons/aena/aena_vacunacion.svg'
import { ReactComponent as CrossIcon } from '@/assets/icons/rounded_cross.svg'
// import { ReactComponent as PendingClockOrangeOutline } from '@/assets/icons/rounded_pending.svg'
import { ReactComponent as PendingClockOrangeOutline } from '@/assets/icons/pending-clock-orange-outline.svg'
import { ReactComponent as StatusOkIcon } from '@/assets/icons/rounded_check.svg'
import { Image, ScrollView, TouchableOpacity, View } from "react-native"
import { t } from 'i18next'
import { useNavigate } from "react-router-dom"
import { usePet, usePetDocumentation, usePetInsurance, useProfile } from '@front/logic'
import { ActiveContainer, ActiveIconBadge, ActiveText, ActiveTextBold, ActiveTextColumn, BenefitContainer, BenefitsContainer, Container, DatacContainer, QRImage } from "@/aena/styles"
import { HeaderWithBackAena } from '@/components/headerwithback'

// Mismo logo que el boton "Pasaporte digital" del /home.
const PassLogo = require('@/assets/icons/pass.png')

interface BenefitItemProps {
  icon: React.JSX.Element
  label: string
  mark: React.JSX.Element
  complete: boolean
  onPress?: () => void
}

const Passport: React.FC = () => {

  const {
    name,
    avatar: photo,
    qrCode: qrUri,
    benefits: petBenefits,
    hasAllVaccines,
    isVaccinated,
    microchip,
    microchipStatus,
    documentsStatus,
    insuranceStatus,
    hasInsurance,
    hasDocument,
    allowPkpass,
  } = useAppSelector((state) => state.selectedPet)
  const { values: valuesProfile, getCountries, handleSubmit: handleSubmitProfile, getProfileContactInfo } = useProfile()

  const navigate = useNavigate()
  const ownerName = valuesProfile.name
  const ownerLastName = valuesProfile.lastName
  const ownerCardNumber = useAppSelector((state) => state.user.ownerCardNumber)
  const ownerEmail = useAppSelector((state) => state.user.email)
  const petGUID = useAppSelector((state) => state.user.selectedPet)
  const chipUrl = useAppSelector((state) => state.petDocumentation.chip.url)
  console.log('passport-ownerEmail', ownerEmail)
  console.log('passport-ownerCardNumber', ownerCardNumber)
  console.log('passport-petGUID', petGUID)

  const { getPetDetails } = usePet()
  const [hasExternalInsurance, setHasExternalInsurance] = React.useState(false)
  const { getInsurance } = usePetInsurance()

  const miHasDocument = useMemo(() => {
    return !!ownerCardNumber && !!ownerName && !!ownerLastName && !!ownerEmail && !!hasDocument && !!chipUrl
  }, [ownerCardNumber, ownerName, ownerLastName, ownerEmail, chipUrl])

  console.log('miHasDocument', miHasDocument, { ownerCardNumber, ownerName, ownerLastName, ownerEmail, chipUrl, hasDocument })

  // Completitud para la nueva logica de pasaporte digital:
  //  - Documentacion NACIONAL (cartilla = microchip) completa cuando hay microchip.
  //  - Documentacion de EXTERIOR (pasaporte UE) completa cuando hay documento de pasaporte.
  const nacionalComplete = !!microchip
  const exteriorComplete = !!hasDocument


  useEffect(() => {
    const fetchData = async () => {
      const hasInsurance = async () => {
        const insurance = await getInsurance()
        console.log('insurance', { insurance })
        setHasExternalInsurance(insurance?.hasInsurance ?? false)
      }
      await getPetDetails()
      await getProfileContactInfo()
      await hasInsurance()
    }
    fetchData()
  }, [])


  const onDocumentPress = () => {
    navigate(`documents`)
  }

  const onVaccinePress = () => {
    navigate(`vaccines`)
  }

  const onInsurancePress = async () => {
    navigate(`insurance`)
  }

  const onExteriorPress = () => {
    navigate(`exterior`)
  }

  const benefits = useMemo(() => {
    if (petBenefits) {
      return [
        {
          icon: <IdBadge width={50} height={50} />,
          label: 'Doc nacional',
          mark: nacionalComplete ? <StatusOkIcon width={25} height={25} /> : <CrossIcon height={25} width={25} />,
          complete: nacionalComplete,
          onPress: onDocumentPress,
        },
        {
          icon: <Vaccine width={50} height={50} />,
          label: t('common.vaccines'),
          mark: isVaccinated ? <StatusOkIcon width={25} height={25} /> : hasAllVaccines ? <PendingClockOrangeOutline width={25} height={25} /> : <CrossIcon height={25} width={25} />,
          complete: isVaccinated,
          onPress: onVaccinePress,
        },
        {
          icon: <Health width={50} height={50} />,
          label: t('common.insurance'),
          mark: insuranceStatus === "VALIDATED" ? <StatusOkIcon width={25} height={25} /> : (hasInsurance || hasExternalInsurance) ? <PendingClockOrangeOutline width={25} height={25} /> : <CrossIcon height={25} width={25} />,
          complete: insuranceStatus === "VALIDATED",
          onPress: onInsurancePress,
        },
      ] as unknown as BenefitItemProps[]
    }
    return []
  }, [petBenefits, petGUID, hasAllVaccines, hasInsurance, miHasDocument, hasExternalInsurance, nacionalComplete, isVaccinated, insuranceStatus])

  // PetPass ID activo = documentacion NACIONAL + vacunas + seguro APROBADO (VALIDATED).
  // Habilita viajes nacionales. (El pasaporte de exterior se exige aparte para vuelos
  // internacionales, no para el "activo".)
  const active = useMemo(() => {
    return nacionalComplete && isVaccinated && insuranceStatus === "VALIDATED"
  }, [nacionalComplete, isVaccinated, insuranceStatus])

  const onPetNamePress = () => {
    navigate('/aena/pet/edit')
  }

  return (
    <React.Fragment>
      <ScrollView>
        <Container style={{ maxWidth: 800, width: '100%', padding: 0, gap: 0 }}>
          <View style={{ flexDirection: 'row', justifyContent: 'center', alignItems: 'center', padding: 16, gap: 16 }}>
            <ResponsiveLogo
              breakpoint={820}
              hysteresis={20}
              large={{ width: 680, height: 96 }}
              small={{ width: 360, height: 48 }}
            />
          </View>
          <HeaderWithBackAena title={t('common.digitalpetpass')} />

          <DatacContainer>
            <PetPicture
              src={photo}
              isWeb
              onStatePress={onPetNamePress}
              showState={true}
              PencilIcon={<PencilIcon width={15} height={15} />}
            />
          </DatacContainer>
          <BenefitsContainer>
            {benefits.map((benefit, index) => (
              <BenefitContainer
                key={index}
                onClick={() => benefit.onPress?.()}
                style={{
                  flex: 1,
                  maxWidth: 160,
                  border: `1px solid ${benefit.complete ? '#57a75f' : '#C7CDD4'}`,
                  borderRadius: 12,
                  padding: '14px 12px',
                  backgroundColor: '#fff',
                  boxSizing: 'border-box',
                }}
              >
                {/* Container relativo para el ícono principal y el lápiz */}
                <View style={{ position: 'relative', marginTop: 10 }}>
                  {benefit.icon}
                  {/* Lápiz posicionado en la esquina superior derecha del ícono */}
                  <View style={{
                    position: 'absolute',
                    top: -12,
                    right: -32,
                    backgroundColor: 'white',
                    borderRadius: 10,
                    padding: 5,
                    borderColor: '#1A2732',
                    borderWidth: 1,
                    shadowColor: '#000',
                    shadowOffset: { width: 0, height: 1 },
                    shadowOpacity: 0.2,
                    shadowRadius: 2,
                    elevation: 2,
                  }}>
                    <PencilIcon width={15} height={15} />
                  </View>
                </View>
                <Text type="cta_medium" numberOfLines={1} style={{ textAlign: 'center', whiteSpace: 'nowrap' } as any}>{benefit.label}</Text>
                {benefit.mark}
              </BenefitContainer>

            ))}
          </BenefitsContainer>

          {/* Boton: documentacion obligatoria para viajes al extranjero (pasaporte UE).
              Gris si no esta completa (NO rojo), verde si esta completa. Lleva al form
              "Pasaporte (opcional)". */}
          <TouchableOpacity
            onPress={onExteriorPress}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 12,
              width: '92%',
              alignSelf: 'center',
              borderWidth: 1,
              borderColor: exteriorComplete ? '#57a75f' : '#C7CDD4',
              borderRadius: 12,
              paddingVertical: 14,
              paddingHorizontal: 16,
              marginTop: 36,
              marginBottom: 6,
              backgroundColor: '#fff',
            }}
          >
            <Image source={PassLogo} style={{ width: 26, height: 36 }} resizeMode="contain" />
            <Text type="cta_medium" style={{ flex: 1, textAlign: 'center' }}>
              {'Documentación obligatoria para viajes al extranjero'}
            </Text>
            {exteriorComplete ? (
              <StatusOkIcon width={25} height={25} />
            ) : (
              <View style={{ width: 22, height: 22, borderRadius: 11, borderWidth: 2, borderColor: '#C7CDD4', backgroundColor: '#EEF0F2' }} />
            )}
          </TouchableOpacity>

          <ActiveContainer active={active}>
            <ActiveIconBadge active={active}>
              {active ? (
                <StatusOkIcon width={32} height={32} />
              ) : (
                <CrossIcon width={32} height={32} />
              )}
            </ActiveIconBadge>
            <ActiveTextColumn>
              <ActiveTextBold type="H3" active={active}>{active ? t('common.petpassidactive') : t('common.petpassidinactive')}</ActiveTextBold>
              <ActiveText type="H5" active={active}>{active ? t('common.alldocumentationcorrect') : t('common.somedocumentationmissing')}</ActiveText>
            </ActiveTextColumn>
          </ActiveContainer>

          {qrUri && (
            <QRImage
              aspectRatio={1}
              source={{
                uri: qrUri,
              }}
              placeHolder={false}
            />
          )}
          {/* <ActiveContainer active={active}>
            <ActiveTextBold type="H3" active={active}>{active ? t('common.petpassidactive') : t('common.petpassidinactive')}</ActiveTextBold>
            <ActiveText type="H5" active={active}>{active ? t('common.alldocumentationcorrect') : t('common.somedocumentationmissing')}</ActiveText>
          </ActiveContainer> */}
        </Container>
      </ScrollView>
    </React.Fragment>
  )
};

export default Passport;